<template>
    <section class="script--menu display--flex">
        <base-card class="script__card" :bodyFullHeight="true">
            <template #card__title >
                <h2 class="script__card__title">{{ script.name }}</h2>
            </template>
            <template #card__body >
                <div v-if="showScript" class="script__card__body position--relative scroll">
                    <div
                        v-for="question in script.questions"
                        :class="[
                            'script__question',
                            { 'display--none': !structureQuestions[question.id].show && isOperateMenu }
                        ]"
                        :data-id="question.id"
                        :key="`question-${question.id}`"
                    >
                        <base-field
                            v-if="question.inputType"
                            :settings="{
                                label: {
                                    class: `capitalize ${componentConfigs[question.inputType.name].labelClass || ''}`,
                                    text: question.question,
                                    isNecessaryPre: true
                                },
                                input: {
                                    component: componentConfigs[question.inputType.name].component,
                                    name: question.id,
                                    id: question.id,
                                    type: componentConfigs[question.inputType.name].type || 'text',
                                    value: structureQuestions[question.id].finalAnswer,
                                    noListener: true,
                                    ...( componentConfigs[question.inputType.name].class && {
                                        class: `script__question__input ${componentConfigs[question.inputType.name].class}`
                                    }),
                                    ...( componentConfigs[question.inputType.name].targetClass && {
                                        targetClass: `script__question__input ${componentConfigs[question.inputType.name].targetClass}`
                                    }),
                                    ...( allowFilling && structureQuestions[question.id].possibleAnswers.length > 1 && {
                                        dynamicData: {
                                            answers: structureQuestions[question.id].possibleAnswers,
                                            data: structureQuestions[question.id].possibleAnswers
                                        }
                                    }),
                                    ...( componentConfigs[question.inputType.name].dynamicData && {
                                        dynamicData: { ...componentConfigs[question.inputType.name].dynamicData }
                                    })
                                },
                                allowFilling
                            }"
                        />
                        <pre v-else class="script__label field__label field__label__pre">{{ question.question }}</pre>
                        <base-field
                            v-if="canShowAdditionalInfo( question.id.toString() )"
                            class="script__question__additional-behaviour"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.additionalAnswer') },
                                input: {
                                    component: 'InputBasic',
                                    id: `additional-behaviour-answer[${ payload[question.id] }]`,
                                    name: `additional-behaviour-answer[${ payload[question.id] }]`,
                                    type: 'text',
                                    class: 'field__input--default typography--default color--default',
                                    value: '',
                                },
                                allowFilling: true
                            }"
                        />
                    </div>
                </div>
            </template>
        </base-card>
    </section>
</template>

<script>
// packages
import {
    reactive,
    toRefs,
    watch,
    computed
} from '@vue/composition-api';

// store
import { mapState, useActions } from '@/hooks/use-store';

// utils
import { transformToInt } from '@/utils/';

export default {
    components: {
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' )
    },
    props: {
        script: {
            type: Object,
            default: () => ({})
        },
        allowFilling: {
            type: Boolean,
            default: true
        },
        isOperateMenu: {
            type: Boolean,
            default: true
        }
    },
    setup ( props ) {
        // definição de vars reactivas.
        const state = reactive({
            structureQuestions: {},
            questionsWithMultipleAnswers: [],
            showScript: false
        });

        /**
         * [method]
         * Validação & associação da árvore a questão com varias possibilidades de resposta.
         * Quando ocorre essa associação, é necessário criar uma nova tree.
         *
         * @param {Boolean} hasMultiplePossibilities
         * @param {Object} structure
         * @param {Object} treeConfigs
         * @param {Number} id
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const associateTreeWithQuestion = ({
            hasMultiplePossibilities,
            structure,
            treeConfigs,
            id
        }) => {
            // Quando é uma resposta com varias possibilidades de resposta
            // é necessário criar a estrutura inicial da árvore.
            if ( hasMultiplePossibilities ) {
                // Caso a questão que esta presente na árvore exista na estrutura
                // de questões, é necessário adicionar a árvore a essa estrutura &
                // dar push no array das questõs com varias possibilidades de resposta.
                if ( structure[treeConfigs.parent] ) {
                    structure[treeConfigs.parent].tree = treeConfigs.directChildren;
                    state.questionsWithMultipleAnswers.push( treeConfigs.parent );
                }

                // iniciação da estrutura da árvore.
                treeConfigs.parent = id;
                treeConfigs.directChildren = {};
            }
        };

        /**
         * [method]
         * Construção da estrutura da árvore da pergunta.
         * É validado se já existe aquela possibilidades de resposta na árvore,
         * caso não exista, é instaceada, caso sim, é dado o push para a árvore.
         * é procedido a validação se a nova questão tem apenas uma possibilidade de
         * resposta, caso isso seja verdade, significa que pertence a árvore que esta
         * a ser tratada, como tal é necessário adicionar essa unlockedQuestion a árvore.
         *
         * @param {Array} questions
         * @param {Object} tree
         * @param {Boolean} hasMultiplePossibilities
         * @param {Number} id
         * @param {Number} answerdId
         * @param {Object} answer
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const buildTree = ({
            questions,
            tree,
            hasMultiplePossibilities = true,
            id,
            answerdId,
            answer
        }) => {
            // caso seja uma resposta simples ( só tenha uma possibilidade de resposta ou null )
            // não é necessário construir a árvore.
            if ( !hasMultiplePossibilities )
                return;

            // validação se esta opção já existe na árvore
            // caso não exista, é instaceada.
            // caso exista, é validado se já existe a unlockQuestionId na árvore
            // de forma a evitar questões duplicadas na árvore.
            if ( !tree.directChildren[answerdId] )
                tree.directChildren[answerdId] = [ answer.unlockQuestionId ];
            else if ( !tree.directChildren[answerdId].includes( answer.unlockQuestionId ) )
                tree.directChildren[answerdId].push( answer.unlockQuestionId );

            // obtenção das possibilidades de reposta da questão.
            if ( answer.unlockQuestionId ) {
                const { possibleAnswers } = questions.find( selectedQuestion => selectedQuestion.id === answer.unlockQuestionId );

                // caso a pergunta só tenha uma possibilidade de resposta
                // significa que ainda pertence a árvore que esta a ser
                // tratada, por isso é necessário fazer o processo de adição
                // a árvore.
                if ( possibleAnswers.length === 1 ) {
                    buildTree({
                        questions,
                        tree,
                        id,
                        answerdId,
                        answer: possibleAnswers[0]
                    });
                }
            }
        };

        /**
         * [computed]
         * verifica se o script é do tipo sequencial
         *
         * @since 1.2.3
         * @category computed
         * @author Jorge Monteiro
         *
         */
        const isSequentialScript = computed( () => props.script?.type?.id === 2 );

        /**
         * [method]
         * Construção da estrutura das respostas
         * possíveis de cada pergunta do guião.
         *
         * @param {Array} questions
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const buildStructureQuestions = questions => {
            const structure = {};
            const treeConfigs = {
                parent: null,
                directChildren: {}
            };
            let unlocked = true;

            questions
                .sort( ( previousQuestion, nextQuestion ) => ( previousQuestion.sequence - nextQuestion.sequence ) )
                .forEach( ({ id, possibleAnswers }, index ) => {
                    // validação se as perguntas devem surgir inicialmente
                    // no guião.
                    const hasNoPossibility = possibleAnswers.length === 0;
                    const hasOnePossibility = possibleAnswers.length === 1;
                    const show = unlocked;
                    unlocked = unlocked && ( hasOnePossibility || isSequentialScript.value );

                    associateTreeWithQuestion({
                        // caso seja a última iteração e não tenha sido associado a árvore.
                        hasMultiplePossibilities: ( !hasNoPossibility && !hasOnePossibility ) || index === questions.length - 1,
                        structure,
                        treeConfigs,
                        id
                    });

                    // criação a estrutura do JSON.
                    structure[id] = {
                        possibleAnswers: [],
                        finalAnswer: null,
                        tree: {},
                        show
                    };

                    const finalAnswer = possibleAnswers
                        .filter( answer => answer.finalAnswer )
                        .map( answer => answer.finalAnswer )
                        .join( ', ' );

                    possibleAnswers.forEach( answer => {
                        structure[id] = {
                            possibleAnswers: [
                                ...structure[id].possibleAnswers,
                                { label: answer.value, value: answer.id, unlockQuestionId: answer.unlockQuestionId }
                            ],
                            finalAnswer: structure[id].finalAnswer || finalAnswer,
                            show
                        };

                        if ( props.isOperateMenu && !isSequentialScript.value ) {
                            buildTree({
                                questions,
                                tree: treeConfigs,
                                hasMultiplePossibilities: ( !hasNoPossibility && !hasOnePossibility ),
                                id,
                                answerdId: answer.id,
                                answer
                            });
                        }
                    });
                });

            state.structureQuestions = structure;
            state.showScript = true;
        };

        watch( () => props.script,
            newScript => {
                if ( newScript.questions )
                    buildStructureQuestions( newScript.questions );
            }, {
                immediate: true
            });

        // armazenado as respostas possíveis na store,
        // de forma a facilitar a construção da resposta para API.
        // Só é armazenado quando é o painel de menu de operador.
        if ( props.isOperateMenu ) {
            const { actions } = useActions();
            actions( 'saveHelper', { structureQuestions: state.structureQuestions });
        }
        // definição das configurações de cada
        // componente, conforme o tipo de input
        // proveniente da API.
        const componentConfigs = {
            checkbox: {
                component: 'InputCheckboxes'
            },
            radiobutton: {
                component: 'InputRadios',
                type: 'radio'
            },
            dropdown: {
                component: 'InputAutocomplete',
                type: 'text',
                targetClass: 'field__input--default typography--default color--default'
            },
            string: {
                component: 'InputBasic',
                class: 'field__input--default typography--default color--default'
            },
            date: {
                component: 'InputCalendar',
                targetClass: 'field__input--default typography--default color--default',
                dynamicData: {
                    configs: {
                        hideInput: true
                    }
                }
            },
            datetime: {
                component: 'InputCalendar',
                targetClass: 'field__input--default typography--default color--default',
                dynamicData: {
                    configs: {
                        mode: 'dateTime',
                        is24hr: true,
                        format: 'YYYY-MM-DD HH:mm',
                        hideInput: true
                    }
                }
            },
            text: {
                component: 'Textarea',
                class: 'field__textarea--default typography--default color--default'
            }
        };

        /**
         * [method]
         * Reset da seleção e do valor das input's que estejam abaixo na árvore do guião
         * em relação a input actualizada pelo cliente e que já estejam anteriormente selecionadas.
         *
         * @param {Number} name
         * @param {Boolean} isNecessaryProceedReset
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const validateAndResetInputValues = ({ name, isNecessaryProceedReset }) => {
            const input = document.querySelector( `.field__radios__input[name="${name}"]:checked` );

            if ( input && isNecessaryProceedReset ) {
                input.checked = false;
                input.value = null;

                // trigger do evento `change`, de forma
                // a limpar na store.
                const event = new Event( 'change' );
                input.dispatchEvent( event );
            }
        };

        /**
         * [method]
         * Conforme a validação é dado o toggle das questões
         * no guião, de forma a que seja definido a sequência
         * do mesmo.
         *
         * @param {Number} questionId
         * @param {String} newAnswer
         * @since 1.0.0
         * @category method
         * @author Tiago Martinho
         *
         */
        const toggleQuestions = ({ currentQuestionId, newAnswer, tree }) => {
            const currentQuestion = state.structureQuestions[currentQuestionId];

            Object.keys( currentQuestion.tree ).forEach( answerId => {
                // validação se a resposta selecionada corresponde
                // a alguma resposta associada a pergunta.
                const show = parseInt( answerId, 10 ) === newAnswer;
                const selectedQuestion = state.structureQuestions[tree.questionId];

                currentQuestion.tree[answerId].forEach( child => {
                    // Caso ainda não tenha sido encontrado a árvore
                    // correspondente a resposta dado pelo utilizador ||
                    // se já foi encontrada && se a pergunta "filho" não está
                    // incluída na árvore escolhida, é necessário proceder ao
                    // toggle dessa questão, conforme a validação anterior.
                    if (
                        !tree.found
                        || ( tree.found
                            && ( selectedQuestion && !selectedQuestion.tree[tree.answerId].includes( child ) )
                        )
                    ) {
                        state.structureQuestions[child] = { // tornar reactivo, de forma a actualizar o HTML
                            ...state.structureQuestions[child],
                            show
                        };
                    }
                });

                // caso seja verdadeiro, quer dizer que
                // foi encontrada a árvore correspondente à
                // resposta do cliente.
                if ( show ) {
                    tree.found = show;
                    tree.answerId = parseInt( answerId, 10 );
                    tree.questionId = parseInt( currentQuestionId, 10 );
                }
            });
        };

        /**
         * [watch]
         * Criação do listener para as perguntas que tem varias
         * possibilidades de resposta, de forma a definir a sequencia
         * do guião conforme a resposta escolhida pelo utilizador.
         *
         * @since 1.0.0
         * @category watch
         * @author Tiago Martinho
         *
         */
        const payload = mapState({ module: 'storePayload', key: 'payload' });
        state.questionsWithMultipleAnswers
            .forEach( questionId => {
                watch( () => payload.value[questionId],
                    newAnswer => {
                        let anOccurrenceHasAlreadyFound = false;
                        const tree = { found: false, id: null };
                        state.questionsWithMultipleAnswers.forEach( qstnId => {
                            // validação se a pergunta é a que foi actualizada
                            // pelo utilizador.
                            const isTheUpdatedQuestion = qstnId === parseInt( questionId, 10 );

                            // Caso a pergunta seja a que foi actualizada pelo utilizador
                            // é necessário validar se é necessário fazer reset as inputs presentes
                            // no guião & dar toggle na sequencia do guião e é dado toggle da flag `anOccurrenceHasAlreadyFound`
                            // de forma a que na próxima iteração seja feita as validações necessárias, de forma a esconder
                            // partes do guião que não correspodem a nova sequencia definida pela a resposta do utilizador.
                            if ( isTheUpdatedQuestion || anOccurrenceHasAlreadyFound ) {
                                validateAndResetInputValues({ name: qstnId, isNecessaryProceedReset: !isTheUpdatedQuestion });
                                toggleQuestions({ currentQuestionId: qstnId, newAnswer: transformToInt( newAnswer ), tree });
                                anOccurrenceHasAlreadyFound = true;
                            }
                        });
                    });
            });

        /**
        *
        * Função que verifica se irá aparecer um campo de 'Info adicional' após selecionar uma pergunta de escolha multipla.
        * O campo deverá aparecer se a pergunta em questão já tiver sido respondida e tiver no payload e se a resposta dada
        * nessa pergunta tiver a key additionalBehaviour a true.
        *
        * */
        const canShowAdditionalInfo = questionId => {
            if ( !Object.keys( payload.value ).includes( questionId ) )
                return false;

            const question = props.script.questions.find( currentQuestion => currentQuestion.id === parseInt( questionId, 10 ) );
            const { possibleAnswers } = question;
            // eslint-disable-next-line
            const answer = possibleAnswers.find( currentAnswer => currentAnswer.id == payload.value[questionId] );
            if ( !answer || !answer.additionalBehaviour )
                return false;

            return true;
        };

        return {
            ...toRefs( state ),
            componentConfigs,
            payload,
            canShowAdditionalInfo
        };
    }
};
</script>

<style lang="scss" scoped >
    .script {
        &--menu {
            justify-content: space-between;
            overflow: hidden;
        }
        &__card {
            &__body {
                height: 100%;
                max-height: 590px;
                margin-right: -30px;
                overflow: auto;
            }
        }
        &__label {
            margin-bottom: 30px;
        }
        &__question {
            max-width: calc( 100% - 30px );
            margin-bottom: 30px;
            /deep/ &__input {
                display: block;
            }
            &__additional-behaviour {
                margin-top: 15px;
            }
        }
    }
</style>